import React, { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Alert, AlertIcon, AlertTitle } from "./components/ui/alert";
import { RiCheckboxCircleFill, RiEyeLine, RiEyeOffLine } from "@remixicon/react";
import { BASE_URL } from "./apiConfig";
import { validateEmail, validatePassword } from "./lib/guardrails";
import { tenantHeaders } from "./lib/tenantConfig";
import ucdpLogo from "./assets/logo3.png";
import bgImage from "./assets/bg-4.png";

const Signup: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [tenantUrl, setTenantUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  const showError = (msg: string) => {
    setErrorMsg(msg);
    setShowErrorAlert(true);
    setTimeout(() => setShowErrorAlert(false), 5000);
  };

  const validateForm = () => {
    if (!email.trim() || !password.trim()) {
      showError("Email and password are required.");
      return false;
    }
    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) { showError(emailCheck.error!); return false; }
    const pwErrors = validatePassword(password);
    if (pwErrors.length > 0) { showError(pwErrors[0]); return false; }
    if (password !== confirmPassword) { showError("Passwords do not match."); return false; }
    return true;
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/user/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...tenantHeaders() },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();

      if (response.ok && data.status === "success") {
        setTenantUrl(data.tenant_url || null);
        setShowSuccessModal(true);
      } else {
        const msg = data.detail || data.message || "Registration failed.";
        if (msg.includes("EMAIL_EXISTS") || msg.includes("email-already-in-use")) {
          showError("An account with this email already exists. Please sign in.");
        } else {
          showError(msg);
        }
      }
    } catch {
      showError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-[#0d0d0d]">

      {/* ── LEFT PANEL — Image + Branding (desktop only) ── */}
      <div className="hidden md:flex relative w-[60%] h-full flex-shrink-0 flex-col">
        <img
          src={bgImage}
          alt="UCDP Discovery Platform"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "70% center" }}
          draggable={false}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.10) 35%, rgba(0,0,0,0.82) 70%, rgba(0,0,0,0.95) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(160deg, rgba(255,84,31,0.28) 0%, transparent 55%)" }}
        />

        {/* Logo */}
        <div className="relative z-10 p-8">
          <Link to="/">
            <img src={ucdpLogo} alt="UCDP" className="h-24 w-auto drop-shadow-lg" />
          </Link>
        </div>

        {/* Bottom content */}
        <div className="relative z-10 mt-auto px-10 pb-14">
          <h2 className="font-ibm-plex-sans font-bold text-[42px] leading-[112%] tracking-[-0.03em] text-white mb-5">
            Discover AI Use Cases<br />Across Every Sector
          </h2>
          <p className="font-lora font-normal text-[13px] leading-[22px] text-[#ffffffa0] mb-10 max-w-[400px]">
            Instantly search, compare, and explore real-world AI applications transforming industries worldwide.
          </p>
          <ul className="flex flex-col gap-[14px]">
            {[
              "Search Use Cases by Sector & Sub-Sector",
              "Discover Startups, VCs & Acquisition Targets",
              "Agentic AI for Sector-Specific Insights",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0 ring-2 ring-[#FF541F]/30"
                  style={{ background: "#FF541F" }}
                />
                <span className="font-inter text-[13px] font-medium text-[#ffffffcc]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── RIGHT PANEL — Signup Form ── */}
      <div
        className="relative flex flex-col w-full md:w-[40%] h-full overflow-y-auto"
        style={{ background: "#0e1016", borderLeft: "1px solid rgba(255,84,31,0.25)" }}
      >
        {/* Top glow */}
        <div
          className="absolute top-0 left-0 right-0 h-[300px] pointer-events-none"
          style={{
            background: "radial-gradient(70% 70% at 50% 0%, rgba(255,84,31,0.12) 0%, transparent 100%)",
          }}
        />

        {/* Mobile logo */}
        <nav className="relative z-10 p-5 md:hidden">
          <Link to="/">
            <img src={ucdpLogo} alt="UCDP" className="h-24 w-auto drop-shadow-lg" />
          </Link>
        </nav>

        {/* Centered form */}
        <div className="relative z-10 flex flex-1 items-center justify-center px-8 py-10">
          <div className="w-full max-w-[340px] flex flex-col gap-6">

            {/* Heading */}
            <div className="flex flex-col gap-[6px]">
              <p className="text-[#FF541F] font-inter font-semibold text-[15px] uppercase tracking-[0.15em] mb-1">
                AI-Powered Discovery Platform
              </p>
              <h3 className="font-inter font-bold text-[28px] leading-[36px] text-white">
                Create account
              </h3>
              <p className="font-inter font-normal text-[14px] text-[#4b5563]">
                Your account starts as Viewer — an admin can upgrade your role.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSignUp} className="flex flex-col gap-4">

              {/* Email */}
              <div className="flex flex-col gap-[6px]">
                <label className="font-inter text-[13px] font-medium text-[#9ca3af]" htmlFor="email">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-[46px] px-4 rounded-[12px] text-white text-[14px] placeholder-[#374151] focus:outline-none focus:ring-1 focus:ring-[#FF541F] transition-all"
                  style={{ background: "#161a23", border: "1px solid #1f2433" }}
                  placeholder="you@company.com"
                  disabled={loading}
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-[6px]">
                <label className="font-inter text-[13px] font-medium text-[#9ca3af]" htmlFor="password">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-[46px] px-4 pr-11 rounded-[12px] text-white text-[14px] placeholder-[#374151] focus:outline-none focus:ring-1 focus:ring-[#FF541F] transition-all"
                    style={{ background: "#161a23", border: "1px solid #1f2433" }}
                    placeholder="Min 8 chars, upper, number, symbol"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4b5563] hover:text-[#9ca3af] focus:outline-none"
                  >
                    {showPassword ? <RiEyeOffLine className="w-5 h-5" /> : <RiEyeLine className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="flex flex-col gap-[6px]">
                <label className="font-inter text-[13px] font-medium text-[#9ca3af]" htmlFor="confirmPassword">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full h-[46px] px-4 pr-11 rounded-[12px] text-white text-[14px] placeholder-[#374151] focus:outline-none focus:ring-1 focus:ring-[#FF541F] transition-all"
                    style={{ background: "#161a23", border: "1px solid #1f2433" }}
                    placeholder="Re-enter your password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4b5563] hover:text-[#9ca3af] focus:outline-none"
                  >
                    {showConfirmPassword ? <RiEyeOffLine className="w-5 h-5" /> : <RiEyeLine className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-[48px] mt-2 rounded-[12px] font-inter font-semibold text-[15px] text-white transition-all hover:scale-[1.02] hover:shadow-2xl active:scale-[0.98] disabled:opacity-60"
                style={{
                  background: "linear-gradient(180deg, #ff6535 0%, #e8430d 100%)",
                  boxShadow: "0 6px 28px rgba(255,84,31,0.45)",
                }}
              >
                {loading ? "Creating account…" : "Create Account"}
              </button>

              <p className="text-center font-inter text-[13px] text-[#6b7280] mt-1">
                Already have an account?{" "}
                <Link to="/login" className="!text-[#FF541F] hover:!text-[#ff6a38] font-medium no-underline">
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* ── Success Modal ── */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div
            className="w-full max-w-md rounded-2xl p-8 flex flex-col items-center gap-5"
            style={{ background: "#0e1016", border: "1px solid rgba(255,84,31,0.25)" }}
          >
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <RiCheckboxCircleFill className="text-green-400 w-9 h-9" />
            </div>
            <h3 className="font-inter font-bold text-[22px] text-white text-center">Check your inbox</h3>
            <p className="font-inter text-[14px] text-[#9ca3af] text-center">
              A verification link has been sent to{" "}
              <span className="text-white font-medium">{email}</span>.
              Click the link to activate your account before logging in.
            </p>

            {tenantUrl && (
              <div className="w-full rounded-xl px-5 py-4 text-center" style={{ background: "#161a23", border: "1px solid #1f2433" }}>
                <p className="font-inter text-[12px] text-[#6b7280] mb-1">Your organisation's login URL</p>
                <span className="font-mono font-semibold text-[#FF541F] text-[15px] tracking-wide">
                  https://{tenantUrl}
                </span>
                <p className="font-inter text-[11px] text-[#4b5563] mt-2">
                  Bookmark this — your team should use this URL to log in.
                </p>
              </div>
            )}

            <button
              onClick={() => navigate("/login")}
              className="w-full h-[46px] rounded-[12px] font-inter font-semibold text-[15px] text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "linear-gradient(180deg, #ff6535 0%, #e8430d 100%)",
                boxShadow: "0 6px 28px rgba(255,84,31,0.45)",
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {showErrorAlert && (
        <div className="fixed top-3 left-1/2 z-50 -translate-x-1/2 w-full max-w-md">
          <Alert variant="destructive" close={true} onClose={() => setShowErrorAlert(false)}>
            <AlertIcon><span className="text-xl">!</span></AlertIcon>
            <AlertTitle>{errorMsg || "Signup failed. Please try again."}</AlertTitle>
          </Alert>
        </div>
      )}
    </div>
  );
};

export default Signup;
